// src/pages/nafed/pacs/generatePacsDocuments.js

import jsPDF from "jspdf";
import { createZipBlob, downloadBlob } from "./simpleZip";

export const PACS_DOCUMENT_TYPES = [
  {
    id: "COMMITTEE_RESOLUTION",
    shortCode: "RES",
    title: "District Registrar Application & Managing Committee Resolution",
  },
  {
    id: "MARGIN_MONEY",
    shortCode: "MMC",
    title: "Margin Money Certificate",
  },
  {
    id: "STATUTORY_PROCEEDINGS",
    shortCode: "SPC",
    title: "No Statutory Proceedings Certificate",
  },
  {
    id: "NO_IRREGULARITY",
    shortCode: "NIC",
    title: "No Financial Irregularity Certificate",
  },
  {
    id: "CONTRIBUTION_UNDERTAKING",
    shortCode: "GIU",
    title: "Godown Insurance Undertaking",
  },
];

const PAGE = {
  width: 595.28,
  height: 841.89,
  left: 48,
  right: 48,
  top: 42,
  bottom: 55,
};

const COLORS = {
  navy: [7, 42, 86],
  blue: [10, 88, 170],
  green: [10, 125, 72],
  orange: [230, 126, 34],
  dark: [28, 39, 56],
  grey: [100, 112, 128],
  lightGrey: [238, 242, 247],
  border: [190, 202, 217],
  white: [255, 255, 255],
  watermark: [225, 230, 236],
};

const clean = (value, fallback = "________________________") => {
  const text = String(value ?? "").trim();
  return text || fallback;
};

const numberValue = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatNumber = (value) => numberValue(value).toLocaleString("en-IN");

const formatDate = (value) => {
  if (!value) return "________________";

  const date =
    value?.toDate instanceof Function ? value.toDate() : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return clean(value);
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const safeFilePart = (value, fallback = "PACS") =>
  clean(value, fallback)
    .replace(/[^a-zA-Z0-9_-]/g, "_")
    .replace(/_+/g, "_")
    .slice(0, 80);

export const PACS_DOCUMENT_DELETE_POLICY = Object.freeze({
  deletableKinds: ["generated", "signed", "evidence"],
  lockedStages: [
    "PMC_UNDER_REVIEW",
    "PMC_DOCUMENTS_APPROVED",
    "MOU_PENDING_PACS_SIGNATURE",
    "PENDING_PMC_SIGNATURE",
    "PENDING_NAFED_SIGNATURE",
    "MOU_READY",
    "FULLY_EXECUTED",
    "MOU_FULLY_EXECUTED",
  ],
});

const deletionRecordUrl = (record = {}) =>
  String(record.url || record.photoUrl || "").trim();

const deletionStoragePath = (record = {}) =>
  String(record.storagePath || record.photoStoragePath || "").trim();

/**
 * Keeps permanent file deletion limited to PACS-owned, pre-submission records.
 * The UI uses the returned reason for an exact disabled-button explanation.
 */
export function canDeletePacsDocumentRecord({
  project = {},
  record = {},
  kind = "",
  currentUid = "",
} = {}) {
  const normalizedKind = String(kind).trim().toLowerCase();
  const stage = String(project.documentStage || "")
    .trim()
    .toUpperCase();
  const status = String(project.status || "")
    .trim()
    .toUpperCase();
  const decision = String(project.pmcDecision || "")
    .trim()
    .toUpperCase();

  if (!PACS_DOCUMENT_DELETE_POLICY.deletableKinds.includes(normalizedKind)) {
    return {
      allowed: false,
      reason: "This record type cannot be deleted.",
    };
  }

  if (!deletionRecordUrl(record) && !deletionStoragePath(record)) {
    return {
      allowed: false,
      reason: "Saved file is not available.",
    };
  }

  if (!String(currentUid).trim()) {
    return {
      allowed: false,
      reason: "Login session is not available.",
    };
  }

  const recordOwner = String(
    record.uploadedByUid ||
      record.generatedByUid ||
      project.pacsUid ||
      project.pacsUserId ||
      project.submittedBy ||
      "",
  ).trim();

  if (recordOwner && recordOwner !== String(currentUid).trim()) {
    return {
      allowed: false,
      reason: "Only the PACS user who created this record can delete it.",
    };
  }

  const archived =
    project.isDeleted === true ||
    project.deleted === true ||
    project.archived === true ||
    project.isArchived === true ||
    ["DELETED", "ARCHIVED", "DELETED_FROM_PMC_ACTIVE_LIST"].includes(
      String(project.archiveStatus || "")
        .trim()
        .toUpperCase(),
    );

  const submitted =
    PACS_DOCUMENT_DELETE_POLICY.lockedStages.includes(stage) ||
    status === "DOCUMENTS_SUBMITTED_TO_PMC" ||
    ["APPROVED", "REJECTED"].includes(decision);

  if (archived) {
    return {
      allowed: false,
      reason: "Archived application files cannot be deleted.",
    };
  }

  if (submitted) {
    return {
      allowed: false,
      reason:
        "This file is locked after PMC submission/decision. Create an audited new version if a correction is required.",
    };
  }

  return { allowed: true, reason: "" };
}

export function buildPacsDocumentDeletionAudit({
  project = {},
  record = {},
  kind = "",
  documentType = "",
  title = "",
  deletedByUid = "",
  deletedByEmail = "",
  deletedAtIso = new Date().toISOString(),
} = {}) {
  return {
    action: "PERMANENT_DELETE",
    source: "PACS_DOCUMENT_CENTER",
    projectId: String(project.id || "").trim(),
    applicationId: String(
      project.applicationId || project.projectId || project.id || "",
    ).trim(),
    documentKind: String(kind).trim().toUpperCase(),
    documentType: String(documentType || record.type || "").trim(),
    title: String(title || record.title || "").trim(),
    fileName: String(record.fileName || record.photoName || "").trim(),
    storagePath: deletionStoragePath(record),
    version: Number(record.version || record.draftVersion || 0),
    signedVersion: Number(record.signedVersion || 0),
    sha256: String(record.sha256 || record.photoSha256 || "").trim(),
    deletedByUid: String(deletedByUid).trim(),
    deletedByEmail: String(deletedByEmail).trim(),
    deletedAtIso,
  };
}

const getPacsName = (data) =>
  clean(
    data.organizationName || data.societyName || data.pacsName,
    "Name of PACS / Cooperative Society",
  );

const getLocation = (data) =>
  [data.village, data.taluka || data.block, data.district, data.state]
    .filter(Boolean)
    .map((value) => String(value).trim())
    .filter(Boolean)
    .join(", ") || "________________________";

const getActName = (data) =>
  clean(
    data.cooperativeAct || data.registrationAct,
    `Applicable Cooperative Societies Act and Rules of ${clean(
      data.state,
      "the concerned State/UT",
    )}`,
  );

const getLandDescription = (data) => {
  const ownership = String(
    data.landOwnershipType || data.landOwnership || "OWNED",
  ).toUpperCase();

  if (ownership === "LEASED") {
    return `registered leased land having a lease period of ${clean(
      data.leaseYears,
      "____",
    )} years`;
  }

  if (ownership === "ALLOTTED") {
    return "land duly allotted to the PACS/Society by the competent authority";
  }

  return "land owned and possessed by the PACS/Society";
};

const documentMeta = (type, data) => {
  const item = PACS_DOCUMENT_TYPES.find((document) => document.id === type);

  if (!item) {
    throw new Error("Invalid PACS document type.");
  }

  const applicationId = clean(data.applicationId || data.projectId, "PACS");

  const financialYear =
    data.financialYear ||
    data.fy ||
    `${new Date().getFullYear()}-${String(new Date().getFullYear() + 1).slice(
      -2,
    )}`;

  const version = Math.max(
    1,
    numberValue(data.documentVersions?.[type] ?? data.documentVersion, 1),
  );

  return {
    ...item,
    applicationId,
    financialYear,
    version,
    documentNumber:
      data.documentNumber ||
      `${safeFilePart(applicationId)}/${item.shortCode}/V${version}`,
  };
};

function bodyFor(type, data) {
  const pacs = getPacsName(data);
  const registrationNumber = clean(data.registrationNumber);
  const capacity = formatNumber(
    data.capacityMt || data.proposedCapacityMt || data.capacity,
  );
  const scheme = clean(
    data.schemeName,
    "World's Largest Grain Storage Plan in the Cooperative Sector",
  );
  const resolutionNumber = clean(data.resolutionNumber);
  const resolutionDate = formatDate(data.resolutionDate);
  const meetingPlace = clean(data.meetingPlace, data.village || data.district);
  const meetingTime = clean(data.meetingTime, "____________");
  const agendaNumber = clean(data.agendaNumber, "____________");
  const contributionRate = formatNumber(data.contributionRate || 2000);
  const totalContribution =
    numberValue(data.capacityMt || data.proposedCapacityMt || data.capacity) *
    numberValue(data.contributionRate || 2000);

  switch (type) {
    case "COMMITTEE_RESOLUTION":
      return {
        referenceTitle: "Extract of Managing Committee Proceedings",
        introRows: [
          ["Resolution Number", resolutionNumber],
          ["Resolution Date", resolutionDate],
          ["Meeting Time", meetingTime],
          ["Meeting Venue", meetingPlace],
          ["Agenda Item Number", agendaNumber],
          [
            "Committee Strength",
            clean(data.totalCommitteeMembers, "________ Members"),
          ],
          ["Members Present", clean(data.membersPresent, "________ Members")],
          [
            "Quorum Status",
            clean(data.quorumStatus, "Quorum duly constituted"),
          ],
        ],
        paragraphs: [
          `A meeting of the Managing Committee of ${pacs}, Registration No. ${registrationNumber}, was held on ${resolutionDate} at ${meetingPlace} under the chairmanship of ${clean(
            data.chairmanName,
          )}. The required quorum was confirmed before taking up the agenda.`,

          `The Managing Committee considered the proposal for construction and development of a ${capacity} MT modern grain storage godown under the "${scheme}" at ${getLocation(
            data,
          )}. The Committee reviewed the proposed land, financial contribution, bank finance requirement and responsibilities of the PACS/Society.`,

          `After detailed deliberation, the Managing Committee unanimously resolved to participate in the Scheme and undertake the proposed project on ${getLandDescription(
            data,
          )}.`,

          `It is further resolved that the PACS/Society shall arrange the required land, margin money, project contribution, statutory permissions, financial records, resolutions, certificates and other supporting documents in accordance with the applicable Scheme guidelines and directions issued by the competent authorities.`,

          `The PACS/Society shall extend full cooperation to NAFED, the designated Project Management Consultant, the financing Bank and other competent authorities for site inspection, survey, soil investigation, design, estimate, DPR preparation, appraisal, tendering, construction, quality monitoring, billing and completion of the project.`,

          `The Chairman and Secretary/Chief Executive Officer are hereby jointly and/or severally authorised to sign and submit applications, declarations, undertakings, agreements, the three-party MOU and other project documents; respond to observations; upload records on the authorised digital portal; and take all necessary actions for implementation of the project.`,

          `RESOLVED UNANIMOUSLY.`,
        ],
        certification:
          "Certified that the above is a true extract of the resolution recorded in the Managing Committee Minutes Book of the PACS/Society.",
      };

    case "MARGIN_MONEY":
      return {
        referenceTitle: "Financial Arrangement Declaration",
        introRows: [
          ["PACS / Society", pacs],
          ["Registration Number", registrationNumber],
          ["Proposed Capacity", `${capacity} MT`],
          [
            "Estimated Project Cost",
            data.estimatedProjectCost
              ? `Rs. ${formatNumber(data.estimatedProjectCost)}`
              : "As per approved DPR/Estimate",
          ],
          [
            "Own Contribution",
            data.ownContribution
              ? `Rs. ${formatNumber(data.ownContribution)}`
              : "As per approved financing pattern",
          ],
          [
            "Bank Finance Required",
            data.bankFinanceRequired
              ? `Rs. ${formatNumber(data.bankFinanceRequired)}`
              : "As per approved financing pattern",
          ],
        ],
        paragraphs: [
          `We, ${pacs}, Registration No. ${registrationNumber}, hereby declare that the PACS/Society has applied for development of a ${capacity} MT grain storage godown under the "${scheme}".`,

          `The PACS/Society confirms that the required margin money, own contribution and any admissible shortfall in project contribution shall be arranged from its own funds, eligible member contribution and/or other lawful sources approved by the competent authority.`,

          `The PACS/Society agrees to deposit the required margin money within the prescribed period and to provide supporting bank statements, resolutions, audited financial records and other evidence whenever required by NAFED, the financing Bank, the designated PMC or another competent authority.`,

          `This declaration is issued pursuant to Managing Committee Resolution No. ${resolutionNumber} dated ${resolutionDate}. It shall remain subject to verification of financial records and acceptance by the financing Bank and competent authorities.`,
        ],
        certification:
          "This is a declaration of financial arrangement by the PACS/Society and does not constitute a sanction or guarantee by NAFED, PMC or the financing Bank.",
      };

    case "STATUTORY_PROCEEDINGS":
      return {
        referenceTitle: "Statutory Status Self-Declaration",
        introRows: [
          ["PACS / Society", pacs],
          ["Registration Number", registrationNumber],
          ["Registered Under", getActName(data)],
          ["Registered Address", clean(data.address)],
          ["Project Location", getLocation(data)],
        ],
        paragraphs: [
          `We, ${pacs}, Registration No. ${registrationNumber}, declare that the PACS/Society is duly registered and functioning under ${getActName(
            data,
          )}.`,

          `Based on the records available with the PACS/Society as on the date of this declaration, no order of liquidation, winding-up, cancellation of registration or other disqualifying statutory order is presently in force against the PACS/Society.`,

          `To the best of the PACS/Society's knowledge and official records, no statutory proceeding is pending which would materially prevent implementation of the proposed grain storage project.`,

          `The PACS/Society undertakes to immediately disclose any material change in the above position during appraisal, financing, tendering, construction or implementation of the project.`,

          `This self-declaration is issued for processing of the application under the "${scheme}" and shall remain subject to independent verification by the Registrar, NAFED, PMC, Bank or another competent authority.`,
        ],
        certification:
          "This document is a PACS/Society self-declaration and shall not be treated as a certificate issued by the Registrar or another statutory authority.",
      };

    case "NO_IRREGULARITY":
      return {
        referenceTitle: "Financial and Governance Self-Declaration",
        introRows: [
          ["PACS / Society", pacs],
          ["Registration Number", registrationNumber],
          [
            "Latest Audit Year",
            clean(data.latestAuditYear, "________________"),
          ],
          [
            "Audit Classification",
            clean(data.auditClassification, "________________"),
          ],
          ["Project Location", getLocation(data)],
        ],
        paragraphs: [
          `We, ${pacs}, Registration No. ${registrationNumber}, hereby declare that, to the best of the knowledge of the authorised office bearers and based on records available with the PACS/Society, no serious financial irregularity, misappropriation, fraud or corruption has been finally established against the PACS/Society which would render it ineligible for the proposed grain storage project.`,

          `The PACS/Society confirms that the information submitted in the application, audited financial statements and supporting records is true and complete to the best of its knowledge.`,

          `The PACS/Society undertakes to disclose any pending audit observation, recovery proceeding, surcharge proceeding, criminal proceeding or other material matter whenever required by the competent authority.`,

          `The PACS/Society further undertakes to immediately report any material change in the above position during appraisal or implementation of the project.`,

          `This self-declaration is issued for processing of the application under the "${scheme}" and shall remain subject to verification by the auditor, Registrar, NAFED, PMC, Bank and other competent authorities.`,
        ],
        certification:
          "This document is a self-declaration and does not replace an audit certificate, Registrar certificate or statutory clearance wherever separately required.",
      };

    case "CONTRIBUTION_UNDERTAKING":
      return {
        referenceTitle: "Financial Contribution Undertaking",
        introRows: [
          ["PACS / Society", pacs],
          ["Registration Number", registrationNumber],
          ["Proposed Capacity", `${capacity} MT`],
          ["Contribution Rate", `Rs. ${contributionRate} per MT`],
          [
            "Indicative Contribution",
            totalContribution > 0
              ? `Rs. ${formatNumber(totalContribution)}`
              : "To be determined",
          ],
          ["Resolution Number", resolutionNumber],
          ["Resolution Date", resolutionDate],
        ],
        paragraphs: [
          `We, ${pacs}, hereby undertake to contribute towards the proposed ${capacity} MT grain storage godown at the rate of Rs. ${contributionRate} per MT, or at such revised rate as may be prescribed under the applicable Scheme guidelines or approved financing pattern.`,

          totalContribution > 0
            ? `Based on the presently proposed capacity and contribution rate, the indicative contribution works out to Rs. ${formatNumber(
                totalContribution,
              )}. The final amount shall be governed by the sanctioned capacity, approved DPR, financing pattern and directions of the competent authority.`
            : `The final contribution amount shall be governed by the sanctioned capacity, approved DPR, financing pattern and directions of the competent authority.`,

          `The PACS/Society agrees to deposit the contribution within the prescribed time and in the manner communicated by NAFED, the financing Bank, the designated PMC or another competent authority.`,

          `The PACS/Society understands that failure to deposit the required contribution or comply with the applicable conditions may result in withholding, suspension or cancellation of further processing, subject to the applicable Scheme guidelines and decision of the competent authority.`,

          `This undertaking is issued pursuant to Managing Committee Resolution No. ${resolutionNumber} dated ${resolutionDate}.`,
        ],
        certification:
          "The contribution amount shown above is indicative and remains subject to the sanctioned project capacity, approved DPR and applicable Scheme guidelines.",
      };

    default:
      throw new Error("Unsupported PACS document type.");
  }
}

function addWatermark(pdf, text = "DRAFT") {
  const normalized = String(text || "").toUpperCase();
  if (["SIGNED", "APPROVED", "FINAL", "VERIFIED"].includes(normalized)) {
    return;
  }
  const width = pdf.internal.pageSize.getWidth();
  const height = pdf.internal.pageSize.getHeight();

  pdf.saveGraphicsState();
  pdf.setTextColor(...COLORS.watermark);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(58);
  pdf.text(text, width / 2, height / 2, {
    align: "center",
    angle: 35,
  });
  pdf.restoreGraphicsState();
}

function addPageHeader(pdf, meta, data, pageNumber) {
  const width = pdf.internal.pageSize.getWidth();

  addWatermark(pdf, clean(data.documentStatus, "DRAFT"));

  pdf.setFillColor(249, 250, 251);
  pdf.rect(0, 0, width, 76, "F");
  pdf.setDrawColor(...COLORS.navy);
  pdf.setLineWidth(1.2);
  pdf.line(PAGE.left, 67, width - PAGE.right, 67);

  pdf.setTextColor(...COLORS.navy);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10.5);
  pdf.text("PACS / COOPERATIVE SOCIETY", PAGE.left, 28);

  pdf.setTextColor(...COLORS.grey);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7.5);
  pdf.text("GRAIN STORAGE PROJECT — OFFICIAL RECORD", PAGE.left, 43);

  pdf.setTextColor(...COLORS.dark);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(8);
  pdf.text(`Application ID: ${meta.applicationId}`, width - PAGE.right, 27, {
    align: "right",
  });

  pdf.setFont("helvetica", "normal");
  pdf.text(`Document No.: ${meta.documentNumber}`, width - PAGE.right, 42, {
    align: "right",
  });

  pdf.setDrawColor(...COLORS.border);
  pdf.setLineWidth(0.6);
  pdf.line(PAGE.left, 88, width - PAGE.right, 88);

  pdf.setTextColor(...COLORS.grey);
  pdf.setFontSize(7.5);
  pdf.text(
    `Version ${meta.version}  |  Financial Year ${meta.financialYear}`,
    PAGE.left,
    101,
  );

  pdf.text(`Page ${pageNumber}`, width - PAGE.right, 101, { align: "right" });
}

function addPageFooter(pdf, meta, data) {
  const width = pdf.internal.pageSize.getWidth();
  const height = pdf.internal.pageSize.getHeight();

  pdf.setDrawColor(...COLORS.border);
  pdf.line(PAGE.left, height - 44, width - PAGE.right, height - 44);

  pdf.setTextColor(...COLORS.grey);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(6.8);

  pdf.text(
    `Generated: ${formatDate(data.generatedDate || new Date())}`,
    PAGE.left,
    height - 30,
  );

  pdf.text(
    `Document No.: ${meta.documentNumber}`,
    width - PAGE.right,
    height - 30,
    { align: "right" },
  );
}

function addAllPageDecorations(pdf, meta, data) {
  const pages = pdf.getNumberOfPages();

  for (let page = 1; page <= pages; page += 1) {
    pdf.setPage(page);
    addPageHeader(pdf, meta, data, page);
    addPageFooter(pdf, meta, data);
  }
}

function ensureSpace(pdf, y, requiredHeight, meta, data) {
  const height = pdf.internal.pageSize.getHeight();

  if (y + requiredHeight <= height - 68) {
    return y;
  }

  pdf.addPage();

  return 126;
}

function addTitle(pdf, title, referenceTitle) {
  const width = pdf.internal.pageSize.getWidth();
  const maxWidth = width - PAGE.left - PAGE.right;

  pdf.setTextColor(...COLORS.navy);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(14);

  const lines = pdf.splitTextToSize(title.toUpperCase(), maxWidth);

  pdf.text(lines, width / 2, 132, {
    align: "center",
  });

  let y = 132 + lines.length * 17;

  pdf.setTextColor(...COLORS.green);
  pdf.setFontSize(8.5);
  pdf.text(referenceTitle.toUpperCase(), width / 2, y, {
    align: "center",
  });

  y += 15;

  pdf.setDrawColor(...COLORS.orange);
  pdf.setLineWidth(2);
  pdf.line(width / 2 - 45, y, width / 2 + 45, y);

  return y + 20;
}

function addInformationTable(pdf, rows, startY, meta, data) {
  const width = pdf.internal.pageSize.getWidth();
  const tableWidth = width - PAGE.left - PAGE.right;
  const labelWidth = 160;
  let y = startY;

  rows.forEach(([label, value]) => {
    const valueLines = pdf.splitTextToSize(
      clean(value),
      tableWidth - labelWidth - 18,
    );

    const rowHeight = Math.max(27, valueLines.length * 11 + 11);

    y = ensureSpace(pdf, y, rowHeight + 3, meta, data);

    pdf.setFillColor(...COLORS.lightGrey);
    pdf.rect(PAGE.left, y, labelWidth, rowHeight, "F");

    pdf.setDrawColor(...COLORS.border);
    pdf.rect(PAGE.left, y, tableWidth, rowHeight);

    pdf.line(PAGE.left + labelWidth, y, PAGE.left + labelWidth, y + rowHeight);

    pdf.setTextColor(...COLORS.dark);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8.3);
    pdf.text(label, PAGE.left + 9, y + 17);

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8.5);
    pdf.text(valueLines, PAGE.left + labelWidth + 9, y + 15);

    y += rowHeight;
  });

  return y + 18;
}

function addParagraphs(pdf, paragraphs, startY, meta, data) {
  const width = pdf.internal.pageSize.getWidth();
  const maxWidth = width - PAGE.left - PAGE.right;
  let y = startY;

  pdf.setTextColor(...COLORS.dark);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9.6);

  paragraphs.forEach((paragraph) => {
    const lines = pdf.splitTextToSize(clean(paragraph, ""), maxWidth);

    const requiredHeight = lines.length * 14 + 12;

    y = ensureSpace(pdf, y, requiredHeight, meta, data);

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9.6);
    pdf.setTextColor(...COLORS.dark);

    pdf.text(lines, PAGE.left, y, {
      align: "justify",
      maxWidth,
      lineHeightFactor: 1.4,
    });

    y += lines.length * 14 + 12;
  });

  return y;
}

function addCertificationBox(pdf, text, startY, meta, data) {
  const width = pdf.internal.pageSize.getWidth();
  const maxWidth = width - PAGE.left - PAGE.right - 24;

  const lines = pdf.splitTextToSize(text, maxWidth);
  const boxHeight = lines.length * 11 + 28;

  let y = ensureSpace(pdf, startY, boxHeight + 12, meta, data);

  pdf.setFillColor(244, 248, 252);
  pdf.setDrawColor(...COLORS.blue);
  pdf.roundedRect(
    PAGE.left,
    y,
    width - PAGE.left - PAGE.right,
    boxHeight,
    5,
    5,
    "FD",
  );

  pdf.setTextColor(...COLORS.blue);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(8);
  pdf.text("CERTIFICATION / IMPORTANT NOTE", PAGE.left + 12, y + 15);

  pdf.setTextColor(...COLORS.dark);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8.2);
  pdf.text(lines, PAGE.left + 12, y + 29);

  return y + boxHeight + 20;
}

function addSignatureSection(pdf, startY, data, meta) {
  const width = pdf.internal.pageSize.getWidth();
  const boxWidth = (width - PAGE.left - PAGE.right - 14) / 2;
  const boxHeight = 105;

  let y = ensureSpace(pdf, startY, boxHeight + 55, meta, data);

  pdf.setTextColor(...COLORS.navy);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9);

  pdf.text(`For ${getPacsName(data)}`, PAGE.left, y);

  y += 13;

  const drawSignatureBox = (x, title, name, designation) => {
    pdf.setDrawColor(...COLORS.border);
    pdf.roundedRect(x, y, boxWidth, boxHeight, 5, 5);

    pdf.setTextColor(...COLORS.grey);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7.5);
    pdf.text("Authorised Signature", x + boxWidth / 2, y + 18, {
      align: "center",
    });

    pdf.setDrawColor(...COLORS.border);
    pdf.setLineDashPattern([2, 2], 0);
    pdf.line(x + 20, y + 62, x + boxWidth - 20, y + 62);
    pdf.setLineDashPattern([], 0);

    pdf.setTextColor(...COLORS.dark);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8.5);
    pdf.text(clean(name), x + boxWidth / 2, y + 77, { align: "center" });

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7.5);
    pdf.text(designation, x + boxWidth / 2, y + 90, { align: "center" });

    pdf.setFont("helvetica", "bold");
    pdf.text(title, x + boxWidth / 2, y + 101, { align: "center" });
  };

  drawSignatureBox(
    PAGE.left,
    "CHAIRMAN",
    data.chairmanName,
    "Managing Committee",
  );

  drawSignatureBox(
    PAGE.left + boxWidth + 14,
    "SECRETARY / CHIEF EXECUTIVE OFFICER",
    data.secretaryName,
    "Authorised Officer",
  );

  y += boxHeight + 15;

  pdf.setTextColor(...COLORS.dark);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);

  pdf.text(`Place: ${clean(data.village || data.district)}`, PAGE.left, y);

  pdf.text(
    `Date: ${formatDate(data.signedDate || data.generatedDate || new Date())}`,
    width / 2,
    y,
  );

  pdf.setDrawColor(...COLORS.border);
  pdf.roundedRect(width - PAGE.right - 90, y - 17, 90, 38, 4, 4);

  pdf.setTextColor(...COLORS.grey);
  pdf.setFontSize(7);
  pdf.text("OFFICIAL PACS SEAL", width - PAGE.right - 45, y + 4, {
    align: "center",
  });

  return y + 30;
}

function addVerificationBlock(pdf, startY, meta, data) {
  const width = pdf.internal.pageSize.getWidth();

  const y = ensureSpace(pdf, startY, 65, meta, data);

  pdf.setFillColor(...COLORS.navy);
  pdf.roundedRect(PAGE.left, y, width - PAGE.left - PAGE.right, 54, 5, 5, "F");

  pdf.setTextColor(...COLORS.white);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(8.2);
  pdf.text("DIGITAL DOCUMENT REFERENCE", PAGE.left + 12, y + 17);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7.3);
  pdf.text(`Application ID: ${meta.applicationId}`, PAGE.left + 12, y + 33);

  pdf.text(`Document Number: ${meta.documentNumber}`, PAGE.left + 12, y + 45);

  pdf.text(
    `Status: ${clean(
      data.documentStatus,
      "DRAFT – SIGNATURES PENDING",
    ).toUpperCase()}`,
    width - PAGE.right - 12,
    y + 33,
    { align: "right" },
  );

  pdf.text(`Version: ${meta.version}`, width - PAGE.right - 12, y + 45, {
    align: "right",
  });
}

export function validatePacsDocumentData(type, data = {}) {
  const errors = [];
  const warnings = [];
  const requireValue = (value, label) => {
    if (!String(value ?? "").trim()) errors.push(`${label} is required.`);
  };

  if (!PACS_DOCUMENT_TYPES.some((item) => item.id === type)) {
    return { valid: false, errors: ["Invalid PACS document type."], warnings };
  }

  requireValue(
    data.organizationName || data.societyName || data.pacsName,
    "PACS / Society name",
  );
  requireValue(data.applicationId || data.projectId, "Application ID");
  requireValue(data.registrationNumber, "Registration number");
  requireValue(data.state, "State");
  requireValue(data.district, "District");
  requireValue(data.chairmanName, "Chairman name");
  requireValue(data.secretaryName, "Secretary / CEO name");

  if (
    !(
      numberValue(data.capacityMt || data.proposedCapacityMt || data.capacity) >
      0
    )
  ) {
    errors.push("Proposed godown capacity must be greater than zero.");
  }

  if (
    [
      "COMMITTEE_RESOLUTION",
      "MARGIN_MONEY",
      "CONTRIBUTION_UNDERTAKING",
    ].includes(type)
  ) {
    requireValue(data.resolutionNumber, "Managing Committee resolution number");
    requireValue(data.resolutionDate, "Managing Committee resolution date");
  }

  if (!data.village && !data.taluka && !data.block) {
    warnings.push("Village / taluka details are not available.");
  }
  if (!data.generatedDate) {
    warnings.push("Generation date will be set automatically.");
  }

  return { valid: errors.length === 0, errors, warnings };
}

export function createPacsDocumentPdf(type, data = {}) {
  const validation = validatePacsDocumentData(type, data);
  if (data.strictValidation === true && !validation.valid) {
    throw new Error(validation.errors.join(" "));
  }

  const preparedData = {
    ...data,
    generatedDate: data.generatedDate || new Date(),
    documentStatus: clean(data.documentStatus, "DRAFT").toUpperCase(),
  };
  const meta = documentMeta(type, preparedData);
  const content = bodyFor(type, preparedData);

  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "a4",
    compress: true,
    putOnlyUsedFonts: true,
  });

  pdf.setProperties({
    title: meta.title,
    subject: `PACS document for Application ${meta.applicationId}`,
    author: getPacsName(preparedData),
    creator: "PACS Project Document Automation",
    keywords: `NAFED, PACS, ${meta.shortCode}, ${meta.applicationId}`,
  });

  let y = addTitle(pdf, meta.title, content.referenceTitle);

  y = addInformationTable(pdf, content.introRows, y, meta, preparedData);

  y = addParagraphs(pdf, content.paragraphs, y, meta, preparedData);

  y = addCertificationBox(pdf, content.certification, y, meta, preparedData);

  y = addSignatureSection(pdf, y, preparedData, meta);

  addVerificationBlock(pdf, y, meta, preparedData);

  addAllPageDecorations(pdf, meta, preparedData);

  const fileName = `${meta.shortCode}_${safeFilePart(
    meta.applicationId,
  )}_V${meta.version}.pdf`;

  return {
    pdf,
    blob: pdf.output("blob"),
    arrayBuffer: pdf.output("arraybuffer"),
    fileName,
    title: meta.title,
    type: meta.id,
    shortCode: meta.shortCode,
    documentNumber: meta.documentNumber,
    version: meta.version,
    status: preparedData.documentStatus,
    pageCount: pdf.getNumberOfPages(),
    generatedAtIso: new Date().toISOString(),
    validation,
  };
}

export function createAllPacsDocumentPdfs(data = {}) {
  const documents = PACS_DOCUMENT_TYPES.map((item) =>
    createPacsDocumentPdf(item.id, data),
  );

  const manifest = {
    applicationId: clean(data.applicationId || data.projectId, "PACS"),
    pacsName: getPacsName(data),
    generatedAtIso: new Date().toISOString(),
    status: clean(data.documentStatus, "DRAFT").toUpperCase(),
    documentCount: documents.length,
    allValid: documents.every((item) => item.validation.valid),
    documents: documents.map((item) => ({
      type: item.type,
      title: item.title,
      shortCode: item.shortCode,
      documentNumber: item.documentNumber,
      version: item.version,
      fileName: item.fileName,
      pageCount: item.pageCount,
      valid: item.validation.valid,
      errors: item.validation.errors,
      warnings: item.validation.warnings,
    })),
  };

  return { documents, manifest };
}

export async function downloadAllPacsDocuments(data = {}) {
  const result = createAllPacsDocumentPdfs(data);
  const entries = result.documents.map((document, index) => ({
    name: `${String(index + 1).padStart(2, "0")}_${document.fileName}`,
    data: document.blob,
  }));
  entries.push({
    name: "manifest.json",
    data: JSON.stringify(result.manifest, null, 2),
  });
  const zip = await createZipBlob(entries);
  const fileName = `PACS_Documents_${safeFilePart(
    result.manifest.applicationId,
  )}.zip`;
  downloadBlob(zip, fileName);
  return result.manifest;
}
